import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import waveContactListSvg from '@/assets/images/svg/wave_element_contact_list.svg';
import { ContactListItem } from './ContactListItem';
import { GroupChatListItem } from './GroupChatListItem';
import { ContactSearch } from './ContactSearch';
import type { ContactListHandle, ContactListProps } from '../types';
import backIcon from '@/assets/images/svg/back_arrow.svg';
import closeIcon from '@/assets/images/svg/close_icon.svg';
import groupChatIcon from '@/assets/images/svg/group_chat.svg';
import { Button } from '@/components/ui/Button';
import { GroupChatHeader } from './GroupChatHeader';
import { useEchoContacts } from '../hooks/useEchoContacts';
import { useGroupChat } from '../hooks/useGroupChat';
import { useConversations } from '@/hooks/useConversations';
import type { ConversationWithUnread, Echo } from '@/types';
import { useConversationStore } from '@/store/conversationStore';

/**
 * Sidebar contact list showing group chats and echo conversations.
 * Supports search filtering, group creation mode, and conversation sorting.
 * Exposes an imperative handle for parent-driven scroll save/restore.
 */
export const ContactList = React.forwardRef<ContactListHandle, ContactListProps>(
  ({ selectedEchoId, onEchoSelect, onOpenSidebar, onClose }, ref) => {
    const navigate = useNavigate();
    const { groupId: activeGroupId } = useParams<{ groupId: string }>();
    const [searchQuery, setSearchQuery] = React.useState('');
    const { echoes, isLoading: isEchoesLoading } = useEchoContacts();
    const { data: serverConversations, isLoading: isConversationsLoading } = useConversations();
    const { conversations, setConversations, mergeFromServer } = useConversationStore();
    const isInitialLoadRef = React.useRef(true);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    React.useImperativeHandle(
      ref,
      () => ({
        getScrollTop: () => scrollContainerRef.current?.scrollTop ?? 0,
        setScrollTop: (top: number) => {
          if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = top;
        },
      }),
      []
    );

    const {
      isCreating,
      selectedEchoes,
      selectedIds,
      groups,
      canSave,
      addEcho,
      removeEcho,
      toggleEcho,
      startCreating,
      cancelCreating,
      saveGroup,
      deleteGroup,
    } = useGroupChat();

    React.useEffect(() => {
      if (!serverConversations) return;
      if (isInitialLoadRef.current) {
        setConversations(serverConversations);
        isInitialLoadRef.current = false;
      } else {
        mergeFromServer(serverConversations);
      }
    }, [serverConversations, setConversations, mergeFromServer]);

    const echoEntries = React.useMemo(() => Object.entries(echoes ?? {}), [echoes]);

    const conversationMap = React.useMemo(() => {
      const map = new Map<string, ConversationWithUnread>();
      conversations.forEach((conv) => {
        map.set(conv.echo_id, conv);
      });
      return map;
    }, [conversations]);

    const sortedEchoEntries = React.useMemo(
      () =>
        echoEntries
          .map(([id, echo]) => {
            const conversation = conversationMap.get(id);
            const time = conversation?.last_message_at
              ? new Date(conversation.last_message_at).getTime()
              : 0;
            const unreadCount = conversation?.unread_count ?? 0;
            return { id, echo, time, unreadCount };
          })
          .sort((left, right) => right.time - left.time),
      [echoEntries, conversationMap]
    );

    const displayList = React.useMemo(() => {
      if (!searchQuery) return sortedEchoEntries;
      const lowerQuery = searchQuery.toLowerCase();
      return sortedEchoEntries.filter((item) => item.echo.name.toLowerCase().includes(lowerQuery));
    }, [sortedEchoEntries, searchQuery]);

    const isLoading = isEchoesLoading || isConversationsLoading;

    const handleContactClick = (id: string, echo: Echo) => {
      if (!isCreating) {
        onEchoSelect(id);
        return;
      }
      toggleEcho(echo);
    };

    return (
      <div className='relative flex h-full flex-col overflow-hidden bg-brand-25 pt-[30px]'>
        <img
          src={waveContactListSvg}
          alt=''
          className='pointer-events-none absolute bottom-0 left-0 z-0 select-none'
        />

        <div className='relative z-20 px-[23px]'>
          {isCreating ? (
            <GroupChatHeader
              echoes={echoes}
              selectedEchoes={selectedEchoes}
              onSelectEcho={addEcho}
              onRemoveEcho={removeEcho}
              onCancel={cancelCreating}
            />
          ) : (
            <>
              <div className='mb-6 flex items-center justify-between'>
                <button
                  onClick={() => onOpenSidebar?.()}
                  className='-ml-2 p-1 transition-opacity hover:opacity-70 xl:hidden'
                >
                  <img src={backIcon} alt='Menu' className='size-6' />
                </button>

                <h2 className='ml-2 flex-1 font-proxima text-[25px] font-extrabold uppercase leading-47 tracking-normal text-brand-800 xl:ml-0 xl:flex-none xl:text-[25px]'>
                  Messages
                </h2>

                <div
                  onClick={startCreating}
                  className='flex cursor-pointer items-center gap-2'
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') startCreating();
                  }}
                >
                  <Button variant='black' size='auto' tabIndex={-1}>
                    Create Group Chat
                  </Button>
                  <img src={groupChatIcon} alt='Group Chat' className='size-6' />
                </div>

                <button
                  onClick={() => onClose?.()}
                  className='p-1 transition-opacity hover:opacity-70 xl:hidden'
                  aria-label='Close messages'
                >
                  <img src={closeIcon} alt='Close' className='size-8' />
                </button>
              </div>

              <ContactSearch value={searchQuery} onChange={setSearchQuery} />
            </>
          )}
        </div>

        <div
          ref={scrollContainerRef}
          className={`custom-scrollbar relative z-10 flex-1 space-y-2 overflow-y-auto overflow-x-hidden pl-[23px] ${
            isCreating && canSave ? 'pb-[90px]' : 'pb-4'
          }`}
        >
          {!isCreating && groups.length > 0 && (
            <>
              <div className='mb-3'>
                <p className='mb-2 ml-[90px] font-sans text-14 font-normal leading-nav tracking-normal text-dark-300 xl:ml-[20px]'>
                  Groups
                </p>
                <div className='ml-[90px] block h-px w-full bg-gray-450 xl:ml-[20px] xl:hidden' />
              </div>
              {groups.map((group) => (
                <GroupChatListItem
                  key={group.id}
                  group={group}
                  isActive={group.id === activeGroupId}
                  onClick={() => navigate(`/messages/group/${group.id}`)}
                  onDelete={deleteGroup}
                />
              ))}
            </>
          )}

          <div className='mb-3'>
            <p className='mb-2 ml-[90px] font-sans text-14 font-normal leading-nav tracking-normal text-dark-300 xl:ml-[20px]'>
              Echo Chats
            </p>
            <div className='ml-[90px] block h-px w-full bg-gray-450 xl:ml-[20px] xl:hidden' />
          </div>

          {isLoading ? (
            <div className='p-4 text-center text-gray-500'>Loading contacts...</div>
          ) : (
            displayList.map(({ id, echo, unreadCount }) => {
              const conv = conversationMap.get(id);
              return (
                <ContactListItem
                  key={id}
                  echo={echo}
                  isActive={isCreating ? selectedIds.has(id) : id === selectedEchoId}
                  onClick={() => handleContactClick(id, echo)}
                  unreadCount={isCreating ? 0 : unreadCount}
                  lastMessageContent={isCreating ? undefined : conv?.last_message_content}
                  lastMessageAt={isCreating ? undefined : conv?.last_message_at}
                  showCheckmark={isCreating}
                />
              );
            })
          )}
        </div>

        {/* Bottom drawer for Save Group */}
        <AnimatePresence>
          {isCreating && canSave && (
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className='absolute inset-x-0 bottom-0 z-30 rounded-t-20 bg-white px-[23px] pb-6 pt-4 shadow-[0_-4px_24px_rgba(0,0,0,0.12)]'
            >
              <div className='mx-auto mb-3 h-1 w-10 rounded-full bg-gray-300' />
              <Button variant='pink' className='w-full' height={49} onClick={saveGroup}>
                Save Group
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

ContactList.displayName = 'ContactList';
